import { collection, onSnapshot, query, where } from "firebase/firestore";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useWorkspaceStore from "stores/useWorkspaceStore";
import {
  buildMeetingReminderKey,
  createMeetingReminderMessage,
  getCurrentReminderThreshold,
  getShownMeetingReminderKeys,
  isReminderForActiveEmbeddedMeeting,
  saveShownMeetingReminderKeys,
  shouldSkipMeetingReminder,
} from "utils/meetingReminders";
import { parseFetchedEvents } from "utils/events";
import { FirestoreDB } from "utils/firebase";
import useReminderClock from "./useReminderClock";

const MAX_VISIBLE_REMINDERS = 3;
const REMINDER_LOOKAHEAD_DAYS = 1;

const getReminderRange = () => ({
  start: moment().startOf("day").toDate().toISOString(),
  end: moment()
    .add(REMINDER_LOOKAHEAD_DAYS, "day")
    .endOf("day")
    .toDate()
    .toISOString(),
  key: moment().format("YYYY-MM-DD"),
});

const sortByStartTime = (a, b) => {
  const aStart = a?.startDateTime || a?.start || 0;
  const bStart = b?.startDateTime || b?.start || 0;

  return new Date(aStart).getTime() - new Date(bStart).getTime();
};

const useUpcomingMeetingReminders = ({ user, enabled = true }) => {
  const [events, setEvents] = useState([]);
  const [visibleReminders, setVisibleReminders] = useState([]);
  const [range, setRange] = useState(() => getReminderRange());
  const shownKeysRef = useRef(getShownMeetingReminderKeys());
  const activeEmbeddedMeeting = useWorkspaceStore(
    (state) => state.activeEmbeddedMeeting
  );
  const now = useReminderClock(15 * 1000);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const nextRange = getReminderRange();
      setRange((currentRange) =>
        currentRange.key === nextRange.key ? currentRange : nextRange
      );
    }, 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!enabled || !user?.uid) {
      setEvents([]);
      return undefined;
    }

    const upcomingEventsQuery = query(
      collection(FirestoreDB, `users/${user.uid}/calendarEvents`),
      where("start.dateTime", ">=", range.start),
      where("start.dateTime", "<=", range.end)
    );

    const unsubscribe = onSnapshot(upcomingEventsQuery, (querySnapshot) => {
      const nextEvents = [];
      querySnapshot.forEach((eventDoc) => {
        nextEvents.push(eventDoc.data());
      });

      setEvents(parseFetchedEvents(user, nextEvents).sort(sortByStartTime));
    });

    return () => unsubscribe();
  }, [enabled, range.end, range.start, user]);

  const { mutedReminderKeys, pendingReminders } = useMemo(() => {
    if (!enabled) {
      return { mutedReminderKeys: [], pendingReminders: [] };
    }

    return events.reduce((result, event) => {
      if (shouldSkipMeetingReminder(event)) return result;
      const thresholdMinutes = getCurrentReminderThreshold(
        event.startDateTime,
        now
      );
      const reminderKey = buildMeetingReminderKey(event, thresholdMinutes);

      if (!thresholdMinutes || !reminderKey) return result;
      if (shownKeysRef.current.has(reminderKey)) return result;
      if (isReminderForActiveEmbeddedMeeting(event, activeEmbeddedMeeting)) {
        result.mutedReminderKeys.push(reminderKey);
        return result;
      }

      result.pendingReminders.push({
        id: reminderKey,
        event,
        thresholdMinutes,
        message: createMeetingReminderMessage({
          event,
          thresholdMinutes,
          activeEmbeddedMeeting,
        }),
      });

      return result;
    }, { mutedReminderKeys: [], pendingReminders: [] });
  }, [activeEmbeddedMeeting, enabled, events, now]);

  useEffect(() => {
    if (mutedReminderKeys.length === 0 && pendingReminders.length === 0) return;

    const shownKeys = new Set(shownKeysRef.current);
    mutedReminderKeys.forEach((reminderKey) => shownKeys.add(reminderKey));
    pendingReminders.forEach((reminder) => shownKeys.add(reminder.id));
    shownKeysRef.current = shownKeys;
    saveShownMeetingReminderKeys(shownKeys);

    if (pendingReminders.length === 0) return;

    setVisibleReminders((currentReminders) => {
      const existingIds = new Set(
        currentReminders.map((reminder) => reminder.id)
      );
      const pendingEventIds = new Set(
        pendingReminders.map((reminder) => reminder.event.eventId)
      );
      const nextReminders = [
        ...currentReminders.filter(
          (reminder) => !pendingEventIds.has(reminder.event.eventId)
        ),
        ...pendingReminders.filter(
          (reminder) => !existingIds.has(reminder.id)
        ),
      ];

      return nextReminders.slice(-MAX_VISIBLE_REMINDERS);
    });
  }, [mutedReminderKeys, pendingReminders]);

  const dismissReminder = useCallback((reminderId) => {
    setVisibleReminders((currentReminders) =>
      currentReminders.filter((reminder) => reminder.id !== reminderId)
    );
  }, []);

  return {
    reminders: visibleReminders,
    dismissReminder,
  };
};

export default useUpcomingMeetingReminders;
